import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAppSession } from './AppSessionProvider';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';

interface AdminSimulateContextValue {
  isSimulating: boolean;
  simulatedUserId: string | null;
  simulatedUsername: string | null;
  stopSimulation: () => void;
}

const AdminSimulateContext = createContext<AdminSimulateContextValue | undefined>(undefined);

const ADMIN_EMAIL = "aaronmichaelfrost@gmail.com";

export function AdminSimulateProvider({ children }: { children: ReactNode }) {
  const { ready, session, isAuthenticated } = useAppSession();
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulatedUserId, setSimulatedUserId] = useState<string | null>(null);
  const [simulatedUsername, setSimulatedUsername] = useState<string | null>(null);

  useEffect(() => {
    if (ready !== 'ready' || !isAuthenticated) {
      return;
    }

    // Only allow admins to simulate
    const userEmail = session?.user?.email;
    if (userEmail !== ADMIN_EMAIL) {
      // Clear any simulation if not admin
      localStorage.removeItem('admin_simulate_user_id');
      setIsSimulating(false);
      setSimulatedUserId(null);
      setSimulatedUsername(null);
      return;
    }

    // Check if we should be simulating
    const simulateUserId = localStorage.getItem('admin_simulate_user_id');
    if (simulateUserId) {
      setIsSimulating(true);
      setSimulatedUserId(simulateUserId);

      // Fetch the simulated user's username
      supabase
        .from('profiles')
        .select('username')
        .eq('id', simulateUserId)
        .single()
        .then(({ data }) => {
          if (data?.username) {
            setSimulatedUsername(data.username);
          }
        });
    } else {
      setIsSimulating(false);
      setSimulatedUserId(null);
      setSimulatedUsername(null);
    }
  }, [ready, isAuthenticated, session]);

  const stopSimulation = () => {
    localStorage.removeItem('admin_simulate_user_id');
    setIsSimulating(false);
    setSimulatedUserId(null);
    setSimulatedUsername(null);
    
    // Reload to clear any cached data
    window.location.href = '/admin';
  };

  return (
    <AdminSimulateContext.Provider
      value={{
        isSimulating,
        simulatedUserId,
        simulatedUsername,
        stopSimulation,
      }}
    >
      {children}
    </AdminSimulateContext.Provider>
  );
}

export function useAdminSimulate() {
  const context = useContext(AdminSimulateContext);
  
  if (context === undefined) {
    throw new Error('useAdminSimulate must be used within an AdminSimulateProvider');
  }
  
  return context;
}
